import { Path } from '../../../modules/Path.js';
import { EnumerateError } from '../../../modules/EnumerateError.js';
import firebase from 'firebase/compat/app';
import { GetTriggeredActions, OverwriteAction } from '../../../modules/ActionTracking.js';
import { splitUpdateIntoGroups } from './SplitUpdateIntoGroups.js';

/** @import { UpdateGroup } from './SplitUpdateIntoGroups.js' */

class NetworkError extends Error {
  constructor(message = 'Network error') {
    super(message);
    this.name = 'NetworkError';
  }
}

// ----------------- Set up global variables for this module -----------------

// The name of the database the user is connected to
let _databaseName;

// The online status of the user
let _userIsOnline = false;

// The current sync key
let _syncKey;

// Promise for the in-flight write cycle (null when no cycle is running)
let _writeCyclePromise = null;

// Promise for the in-flight pending→outgoing promotion (null when none running)
let _promotePendingChangesPromise = null;

// FIFO queue serializing all reads/writes to `$pending_changes` so writes and promotion cycles
// never overlap.
/** @type {Promise<any>} */
let _pendingChangesOperationQueue = Promise.resolve();

// FIFO queue serializing all reads/writes to `$outgoing_changes` so promotion merges, per-path
// clears, and job-level clears never overlap.
/** @type {Promise<any>} */
let _outgoingChangesOperationQueue = Promise.resolve();

// Flag for if firebase has rejected an update due to invalid paths
let _shouldValidatePathsToSync = false;

// Action tracking registrations per job id
const _jobsWithRegisteredActionTracking = {};

// TODO: Type this guy similar to UploadProgressInfo in PhotoUploadService.js
let _updateGroupProgress = {};

let _progressGroupListeners = {};

let updateGroupsThatErrored = new Set();

// ----------------- Define setter functions for module variables -----------------

export async function init(dbName) {
  _databaseName = dbName;

  // Ensure any pending changes are promoted to outgoing changes and then progress is initialized for all update groups.
  await promotePendingChangesToOutgoingChanges();

  await rebuildProgressGroups();

  tryToWritePendingChanges();
}

export function setOnlineStatus(onlineStatus) {
  _userIsOnline = onlineStatus;
  tryToWritePendingChanges();
}

export function registerActionTrackingForJob(actionTrackingModels, companyId, jobId) {
  if (!jobId) return;

  _jobsWithRegisteredActionTracking[jobId] = {
    actionTrackingModels: actionTrackingModels ?? [],
    actionTrackingCompanyId: companyId ?? ''
  };

  // Clear any errored update groups for this job so they get retried now that tracking is resolved
  for (const erroredGroupId of updateGroupsThatErrored) {
    if (erroredGroupId === jobId || erroredGroupId.startsWith(`${jobId}:`)) updateGroupsThatErrored.delete(erroredGroupId);
  }

  // Some pending changes may be waiting on tracking resolution for this job, try to write them now
  tryToWritePendingChanges();
}

export function setSyncKey(localSyncKey) {
  _syncKey = localSyncKey;
}

// ----------------- Define getter functions for module variables -----------------

export function getSyncKey() {
  return _syncKey;
}

// ----------------- Define public facing functions for module -----------------

export function listenToAllUnsavedChanges(callback) {
  const listenerId = firebase.database().ref().push().key;
  _progressGroupListeners[listenerId] = { callback };
  // Notify the callback for all progress groups.
  callback(_updateGroupProgress);

  const disconnect = () => delete _progressGroupListeners[listenerId];
  return disconnect;
}

export function listenToUnsavedChangesForGroup(progressGroupId, callback) {
  const listenerId = firebase.database().ref().push().key;
  _progressGroupListeners[listenerId] = { progressGroupId, callback };

  const progressGroup = _updateGroupProgress[progressGroupId] ?? getEmptyProgressGroup();
  callback(progressGroup);

  const disconnect = () => delete _progressGroupListeners[listenerId];
  return disconnect;
}

/**
 * Promotes the pending changes to outgoing changes, then tries to write them to Firebase.  Once it completes, it checks for any new
 * pending changes and then writes those the same way until there are no more.
 *
 */
export async function tryToWritePendingChanges() {
  // Ensure firebase is initialized and the user is authenticated before continuing.
  const firebaseIsInitialized = firebase.apps.length > 0;
  if (!firebaseIsInitialized) return;

  const userIsAuthenticated = firebase.auth().currentUser != null;
  if (!userIsAuthenticated) return;

  // If the user is offline, we are already writing, or the database is not initialized, do not attempt to write to Firebase
  if (!_userIsOnline || !_databaseName) return;

  // Wait for an ongoing write cycle to finish before starting a new one.
  if (_writeCyclePromise) await _writeCyclePromise;
  // NOTE: Once the write cycle finishes, `_writeCyclePromise` will be cleared.
  // But if multiple callers were awaiting the previous cycle and we were not
  // first in line, whoever was first in line would have already started a new
  // cycle and set `_writeCyclePromise` again. So check one more time to see if
  // there's a cycle in-flight. If so, join it instead of starting a new one.
  if (_writeCyclePromise) return _writeCyclePromise;

  _writeCyclePromise = (async () => {
    try {
      // Loop until nothing's left to write; concurrent callers await this promise instead of racing.
      while (true) {
        // TODO (01/29/2026): Remove this code once "$update_1" and "$update_2" are no longer being used
        // If there are updates in any of the legacy ledgers, copy them to the new pending changes structure
        await copyLegacyLedgerUpdatesToPendingChanges();

        await promotePendingChangesToOutgoingChanges();

        // Get the user auth token for writing to Firebase
        const userAuthToken = await firebase.auth().currentUser.getIdToken();

        // Check if there are changes to write to Firebase that have not already errored
        const outgoingChanges = await getOutgoingChanges();
        const hasOutgoingChangesToWrite = shouldWriteOutgoingChanges(outgoingChanges);

        // If there are no outgoing changes to write, it means everything is synced so finish off the write process
        if (!hasOutgoingChangesToWrite) {
          await finishWritingChanges(userAuthToken);
          return;
        }

        // Set a flag that says that data is being synced currently
        globalThis.FirebaseIDB.fm.set('syncing', true);

        // Write the outgoing changes to Firebase. Loop again afterwards to pick up
        // any new pending changes that arrived during current pass.
        await writeOutgoingChanges(outgoingChanges, userAuthToken);
      }
    } catch (err) {
      // If the error was a network error, quietly fail and wait for the next online event to try again.
      // Otherwise, re-throw the error.
      if (!(err instanceof NetworkError)) throw err;
    } finally {
      _writeCyclePromise = null;
    }
  })();

  return _writeCyclePromise;
}

/**
 * Determine if there are outgoing changes that need to be written to Firebase that have not already errored
 * @param {object} outgoingChanges - The current outgoing changes to be written to Firebase
 * @returns {boolean} - True if there are outgoing changes that need to be written to Firebase, false otherwise
 */
function shouldWriteOutgoingChanges(outgoingChanges) {
  if (Object.keys(outgoingChanges).length === 0) return false;

  // Split the outgoing changes into update groups and check if there are any groups that have not already errored
  const updateGroups = splitUpdateIntoGroups(outgoingChanges);
  const updateGroupsWithNoErrors = updateGroups.filter((group) => !updateGroupsThatErrored.has(group.id));
  return updateGroupsWithNoErrors.length != 0;
}
/**
 * Updates the local offline data with the new data if the path is an offline path, then appends the update to the pending changes
 * @param {string} updateDatabasePath - The database path for the update
 * @param {object} updateData - The data that will get written to the database path
 * @param {Object} [options={}] - Additional options for the update operation.
 * @param {boolean} [options.bypassInit] - Determines whether to wait for initialization
 */
export async function pushUpdateToPendingChanges(updateDatabasePath, updateData, options = {}) {
  // Wait for FirebaseIDB to be initialized before the update is processed unless an option bypasses this
  if (!options.bypassInit && globalThis.FirebaseIDB.initializing) await globalThis.FirebaseIDB.initializing;

  // Update the sync key
  updateSyncKey();

  // Create an array to hold all execution promises and an object to hold the update to notify
  const executionPromises = [];
  const updateToNotify = {};

  // Map each path in the update data to its full path relative to the database
  const updateDataPathsRelativeToDatabase = Object.entries(updateData ?? {}).map(([updatePath, dataAtPath]) => ({
    path: Path.join([updateDatabasePath, updatePath], '/'),
    data: dataAtPath
  }));

  // Determine the paths to write to pending changes based on if the update has data or not
  const pathDataToWriteToPendingChanges =
    updateData != null ? updateDataPathsRelativeToDatabase : [{ path: updateDatabasePath, data: updateData }];

  // Write each path to the write collection and the IDB if the path is offline.
  for (const pathData of pathDataToWriteToPendingChanges) {
    const { path: pathRelativeToTheDatabase, data: dataToPush } = pathData;

    Path.set(updateToNotify, pathRelativeToTheDatabase, dataToPush, '/');

    // Queue the IDB write so it's serialized against any in-flight promotion.
    const pendingChangesPath = Path.join(['$pending_changes', pathRelativeToTheDatabase], '/');
    const pushToPendingChangesPromise = queuePendingChangesOperation(() =>
      globalThis.FirebaseIDB.set(pendingChangesPath, dataToPush, { allowEmpty: true })
    );
    executionPromises.push(pushToPendingChangesPromise);
  }

  // Set the unsaved changes flag.
  globalThis.FirebaseIDB.fm.set('unsavedChanges', true);

  // Wait for all data to be staged and updated properly
  await Promise.all(executionPromises);

  // Split the update into groups to notify progress.
  const updateGroups = splitUpdateIntoGroups(updateToNotify);
  updateGroups.forEach((updateGroup) => {
    if (Object.keys(updateGroup.update).length > 0) setUpdateGroupProgress(updateGroup, { complete: false });
  });

  // Clear any errors with existing update groups
  const effectedUpdateGroupKeys = updateGroups.map((updateGroup) => updateGroup.id);
  effectedUpdateGroupKeys.forEach((groupId) => updateGroupsThatErrored.delete(groupId));
}

/**
 * Gets all unsaved changes for the job
 * @param {string} jobId - The ID of the job to get unsaved changes for.
 * @returns {Promise<object>} - An object containing all unsaved changes for the job.
 */
export async function getUnsavedChangesForJob(jobId) {
  // Get the current pending changes
  const currentPendingChanges = await getPendingChanges();

  // Get the current outgoing changes
  const currentOutgoingChanges = await getOutgoingChanges();

  // Combine pending and outgoing changes
  const unsavedChanges = addPendingChangesToOutgoingChanges(currentPendingChanges, currentOutgoingChanges);

  // Extract the changes for the specified job id and return them
  const unsavedChangesForJob = extractJobChanges(unsavedChanges, jobId);
  return unsavedChangesForJob;
}

/**
 * Clears all the pending and outgoing changes for a specific job by removing all relevant entries
 * @param {string} jobId - The ID of the job for which to clear changes.
 */
export async function clearUnsavedChangesForJob(jobId) {
  // Queue the pending changes modification so it doesn't overlap with another pending changes call.
  await queuePendingChangesOperation(async () => {
    const pendingChanges = await getPendingChanges();
    const hasPendingChanges = Object.keys(pendingChanges).length != 0;
    if (!hasPendingChanges) return;

    const jobPendingChanges = extractJobChanges(pendingChanges, jobId);

    // Remove the job changes from pending changes
    const flattenedJobChanges = Path.flattenObject(jobPendingChanges, '/');
    Object.keys(flattenedJobChanges).forEach((path) => Path.delete(pendingChanges, path, '/'));
    await overwritePendingChanges(pendingChanges);
  });

  // Queue the outgoing changes modification so it doesn't overlap with another outgoing changes call.
  await queueOutgoingChangesOperation(async () => {
    const outgoingChanges = await getOutgoingChanges();
    const hasOutgoingChanges = Object.keys(outgoingChanges).length != 0;
    if (!hasOutgoingChanges) return;

    const jobOutgoingChanges = extractJobChanges(outgoingChanges, jobId);

    // Remove the job changes from outgoing changes
    const flattenedJobOutgoingChanges = Path.flattenObject(jobOutgoingChanges, '/');
    Object.keys(flattenedJobOutgoingChanges).forEach((path) => Path.delete(outgoingChanges, path, '/'));
    await overwriteOutgoingChanges(outgoingChanges);
  });

  // Clear all update groups related to this job
  clearProgressGroup(jobId);
}

// ----------------- Define internal functions for module -----------------

/**
 * Extract the changes related to a specific job from a larger set of changes.
 * @param {object} changes - The full set of changes.
 * @param {string} jobId - The job ID to filter changes for.
 * @returns {object} The filtered changes related to the specified job.
 */
function extractJobChanges(changes, jobId) {
  // Split the update into groups
  const splitUpdateGroups = splitUpdateIntoGroups(changes);

  // Filter out groups that are not related to the job
  // TODO (02/09/2026): If there are any groups that don't start with the job id, this logic will need to be updated
  const jobUpdateGroups = splitUpdateGroups.filter((group) => group.type !== 'DEFAULT' && group.id.startsWith(jobId));

  // Rebuild the update object
  const jobChanges = {};
  for (const updateGroup of jobUpdateGroups) {
    const { update } = updateGroup;
    if (!update) continue;

    // Apply the update paths to the rebuilt job changes object
    const flattenedJobChanges = Path.flattenObject(update, '/');
    Object.keys(flattenedJobChanges).forEach((path) => Path.set(jobChanges, path, flattenedJobChanges[path], '/'));
  }

  return jobChanges;
}

/**
 * Returns a new progress group object with all properties initialized to their default values.
 */
export function getEmptyProgressGroup() {
  return {
    numUpdates: 0,
    numUpdatesRemaining: 0,
    numUpdatesFailed: 0,
    numUpdatesComplete: 0,
    progress: 0,
    complete: true,
    completedAt: null,
    incompleteAt: null,
    errors: [],
    failed: false,
    updateGroupProgress: {}
  };
}

/**
 *
 * @param {UpdateGroup} updateGroup
 * @param {Record<string, any>} progressUpdates
 */
function setUpdateGroupProgress(updateGroup, progressUpdates) {
  // Determine the progress group id which is used to group together the progress for multiple update groups.
  // Currently, all job-related update groups are grouped under the job id, while other update groups are grouped under 'default'.
  const progressGroupId = updateGroup?.matches?.groups?.jobId ?? 'default';

  // Get (and create if necessary) the progress object for this group.
  const progressGroup = (_updateGroupProgress[progressGroupId] ??= getEmptyProgressGroup());

  // If the progress group was marked as complete 1 second ago (to avoid UI flicker), remove any completed update groups.
  if (progressGroup.complete && progressGroup.completedAt != null && Date.now() - progressGroup.completedAt > 1000) {
    for (const updateGroupId in progressGroup.updateGroupProgress) {
      delete progressGroup.updateGroupProgress[updateGroupId];
    }
  }

  // Get (and create if necessary) the progress object for this update group.
  const updateGroupProgress = (progressGroup.updateGroupProgress[updateGroup.id] ??= {});

  // Update the progress object with the new progress data.
  Object.assign(updateGroupProgress, progressUpdates);

  const wasComplete = progressGroup.complete;

  // Update the progress group to reflect the combined progress of all update groups in this progress group.
  const updateGroupsArray = Object.values(progressGroup.updateGroupProgress);
  const numUpdates = updateGroupsArray.length;
  const numUpdatesRemaining = updateGroupsArray.filter((x) => !x.complete).length;
  const numUpdatesFailed = updateGroupsArray.filter((x) => x.error).length;
  const numUpdatesComplete = updateGroupsArray.filter((x) => x.complete).length;
  const progress = numUpdates - numUpdatesFailed === 0 ? 0 : (numUpdatesComplete * 100) / (numUpdates - numUpdatesFailed);
  const complete = numUpdatesRemaining === 0;
  const errors = updateGroupsArray.filter((x) => x.error).map((x) => x.error);
  const failed = errors.length > 0;
  Object.assign(progressGroup, {
    numUpdates,
    numUpdatesRemaining,
    numUpdatesFailed,
    numUpdatesComplete,
    progress,
    complete,
    errors,
    failed
  });

  // Update timestamps when transitioning between complete and incomplete states.
  // TODO (2/12/2026): This isn't quite correct since the true incomplete time would be when this job first had unsaved changes (not necessarily this session or even this day).
  // Since we hold back server data for a job, however, I believe the synced time for the job in the MobileOfflineJobManager.js will hide this error.
  if (wasComplete && !complete) {
    progressGroup.incompleteAt = Date.now();
    progressGroup.completedAt = null;
  } else if (!wasComplete && complete) {
    progressGroup.incompleteAt = null;
    progressGroup.completedAt = Date.now();
  }

  notifyProgressGroupChanges(progressGroupId);
}

function clearProgressGroup(progressGroupId) {
  _updateGroupProgress[progressGroupId] = getEmptyProgressGroup();
  notifyProgressGroupChanges(progressGroupId);
}

function notifyProgressGroupChanges(progressGroupId) {
  const progressGroup = _updateGroupProgress[progressGroupId];
  for (const listenerId in _progressGroupListeners) {
    const listener = _progressGroupListeners[listenerId];

    const notifyListenerOfAllGroups = listener.progressGroupId == null;

    // If the listener is not subscribed to a specific progress group, notify them of the progress for this group.
    if (notifyListenerOfAllGroups) {
      listener.callback({ [progressGroupId]: progressGroup });
      continue;
    }

    // Don't notify this listener if they are subscribed to a different progress group.
    if (listener.progressGroupId !== progressGroupId) continue;

    // Otherwise, notify the listener of the progress for this group.
    listener.callback(progressGroup);
  }
}

/**
 * Fully rebuilds the progress groups.
 */
export async function rebuildProgressGroups() {
  // Clear all progress groups.
  for (const progressGroupId in _updateGroupProgress) clearProgressGroup(progressGroupId);
  // Get unsaved changes, split into update groups, and rebuild progress groups from them.
  const unsavedChanges = await getCombinedPendingAndOutgoingChanges();
  const updateGroups = splitUpdateIntoGroups(unsavedChanges);
  updateGroups.forEach((updateGroup) => setUpdateGroupProgress(updateGroup, { complete: false }));
}

// TODO (01/29/2026): Remove this code once "$update_1" and "$update_2" are no longer being used
async function copyLegacyLedgerUpdatesToPendingChanges() {
  // Get the data in the legacy ledgers
  const dataInUpdate1 = await globalThis.FirebaseIDB.get(`$update_1/${_databaseName}`);
  const update1HasData = Object.keys(dataInUpdate1 ?? {}).length != 0;
  const dataInUpdate2 = await globalThis.FirebaseIDB.get(`$update_2/${_databaseName}`);
  const update2HasData = Object.keys(dataInUpdate2 ?? {}).length != 0;
  if (!update1HasData && !update2HasData) return;

  // Queue the pending changes modification so it doesn't overlap with another pending changes call.
  await queuePendingChangesOperation(async () => {
    const currentPendingChanges = await getPendingChanges();

    // Create a new object to hold the combined pending changes
    const combinedPendingChanges = currentPendingChanges ?? {};

    // Copy data from $update_1 to the combined pending changes
    const flattenedDataInUpdate1 = Path.flattenObject(dataInUpdate1 ?? {}, '/');
    Object.entries(flattenedDataInUpdate1).forEach(([path, value]) => {
      Path.set(combinedPendingChanges, path, value, '/');
    });

    // Copy data from $update_2 to the combined pending changes
    const flattenedDataInUpdate2 = Path.flattenObject(dataInUpdate2 ?? {}, '/');
    Object.entries(flattenedDataInUpdate2).forEach(([path, value]) => {
      Path.set(combinedPendingChanges, path, value, '/');
    });

    // Set the combined pending changes to the IDB
    await overwritePendingChanges(combinedPendingChanges);
  });

  // Clear out the legacy ledgers
  if (update1HasData) await globalThis.FirebaseIDB.set(`$update_1/${_databaseName}`, null);
  if (update2HasData) await globalThis.FirebaseIDB.set(`$update_2/${_databaseName}`, null);
}

function updateSyncKey() {
  // Generate new sync key.
  _syncKey = firebase.database().ref().push().key;
}

/**
 * Returns a new object containing the outgoing changes with the pending changes merged in. Does not mutate either input.
 * @param {object} pendingChanges - The current pending changes to be promoted.
 * @param {object} outgoingChanges - The current outgoing changes to be added to.
 * @return {object} A new object containing the merged changes.
 */
function addPendingChangesToOutgoingChanges(pendingChanges, outgoingChanges) {
  const newOutgoingChanges = structuredClone(outgoingChanges ?? {});
  const flattenedPendingChanges = Path.flattenObject(pendingChanges ?? {}, '/');
  Object.entries(flattenedPendingChanges).forEach(([path, value]) => {
    Path.set(newOutgoingChanges, path, value, '/');
  });
  return newOutgoingChanges;
}

/**
 * Combines the pending changes and outgoing changes to get the full set of unsaved changes that need to be written to Firebase.
 * @returns {Promise<object>} The combined pending and outgoing changes.
 */
async function getCombinedPendingAndOutgoingChanges() {
  // Get the current pending changes
  const currentPendingChanges = await getPendingChanges();

  // Get the current outgoing changes
  const currentOutgoingChanges = await getOutgoingChanges();

  // Add the pending changes to the outgoing changes
  const newOutgoingChanges = addPendingChangesToOutgoingChanges(currentPendingChanges, currentOutgoingChanges);
  return newOutgoingChanges;
}

/**
 * Append an operation to the `$pending_changes` FIFO queue. Rejections are swallowed on the
 * chain so a failed op doesn't break the queue.
 * @template T
 * @param {() => Promise<T>} operation
 * @returns {Promise<T>}
 */
function queuePendingChangesOperation(operation) {
  const next = _pendingChangesOperationQueue.then(operation, operation);
  _pendingChangesOperationQueue = next.catch(() => {});
  return next;
}

/**
 * Append an operation to the `$outgoing_changes` FIFO queue.
 * @template T
 * @param {() => Promise<T>} operation
 * @returns {Promise<T>}
 */
function queueOutgoingChangesOperation(operation) {
  const next = _outgoingChangesOperationQueue.then(operation, operation);
  _outgoingChangesOperationQueue = next.catch(() => {});
  return next;
}

/**
 * Promote all pending changes to outgoing changes. Runs through the pending-changes queue so no
 * writes land between the read and the wipe. Concurrent callers join the in-flight promotion via
 * `_promotePendingChangesPromise`.
 */
async function promotePendingChangesToOutgoingChanges() {
  // If a promotion is already scheduled/running, join it.
  if (_promotePendingChangesPromise) return _promotePendingChangesPromise;

  // Publish synchronously so concurrent callers dedupe onto this promise.
  _promotePendingChangesPromise = queuePendingChangesOperation(async () => {
    const currentPendingChanges = await getPendingChanges();
    if (Object.keys(currentPendingChanges).length === 0) return;

    // Queue the outgoing changes modification so it doesn't overlap with another outgoing changes call.
    await queueOutgoingChangesOperation(async () => {
      const currentOutgoingChanges = await getOutgoingChanges();
      const newOutgoingChanges = addPendingChangesToOutgoingChanges(currentPendingChanges, currentOutgoingChanges);
      await overwriteOutgoingChanges(newOutgoingChanges);
    });

    // Safe to wipe the bucket: writes queued during promotion will run after this and seed the
    // next promotion cycle.
    await globalThis.FirebaseIDB.set(`$pending_changes/${_databaseName}`, null);
  }).finally(() => {
    _promotePendingChangesPromise = null;
  });

  return _promotePendingChangesPromise;
}

/**
 * Gets the current outgoing changes from IndexedDB.
 * @returns {Promise<Record<string, any>>}
 */
async function getOutgoingChanges() {
  if (!_databaseName) throw new Error('Missing database name');

  const outgoingChangesPath = `$outgoing_changes/${_databaseName}`;
  const outgoingChanges = await globalThis.FirebaseIDB.get(outgoingChangesPath);
  return outgoingChanges ?? {};
}

/**
 * Overwrites the outgoing changes in IndexedDB.
 * @returns {Promise<void>}
 */
async function overwriteOutgoingChanges(outgoingChanges) {
  if (!_databaseName) throw new Error('Missing database name');

  const outgoingChangesPath = `$outgoing_changes/${_databaseName}`;
  await globalThis.FirebaseIDB.set(outgoingChangesPath, outgoingChanges);
}

/**
 * Gets the current pending changes from IndexedDB.
 * @returns {Promise<Record<string, any>>}
 */
async function getPendingChanges() {
  if (!_databaseName) throw new Error('Missing database name');

  const pendingChangesPath = `$pending_changes/${_databaseName}`;
  const pendingChanges = await globalThis.FirebaseIDB.get(pendingChangesPath);
  return pendingChanges ?? {};
}

/**
 * Overwrites the pending changes in IndexedDB.
 * @returns {Promise<void>}
 */
async function overwritePendingChanges(pendingChanges) {
  if (!_databaseName) throw new Error('Missing database name');

  const pendingChangesPath = `$pending_changes/${_databaseName}`;
  await globalThis.FirebaseIDB.set(pendingChangesPath, pendingChanges);
}

/**
 * Write the outgoing changes in similar groups to Firebase using the REST API
 * @param {Object} outgoingChanges - The current outgoing changes to be written
 * @param {string} userAuthToken - The authentication token for Firebase.
 */
async function writeOutgoingChanges(outgoingChanges, userAuthToken) {
  // Split the update into groups to write to Firebase
  const updateGroups = splitUpdateIntoGroups(outgoingChanges);

  for (const updateGroup of updateGroups) {
    // If there's no update in this group, skip it
    const update = updateGroup.update ?? {};
    if (Object.keys(update).length === 0) {
      setUpdateGroupProgress(updateGroup, { complete: true });
      continue;
    }

    // Split the update into priority and non-priority updates and flatten/sanitize them
    const { flattenedNormalUpdate, flattenedPriorityUpdate } = flattenAndSplitUpdateByPriority(update);

    // Pull the job id off the group so its action tracking can be verified as resolved
    const options = { processActionTrackingForJobId: updateGroup.matches?.groups?.jobId };

    try {
      // Write the normal update first
      if (Object.keys(flattenedNormalUpdate).length != 0) {
        await writeFlattenedUpdateToFirebase(flattenedNormalUpdate, userAuthToken, options);
      }

      // Write the priority update second (this needs to happen second because this is how Firebase expects priority updates)
      if (Object.keys(flattenedPriorityUpdate).length != 0) {
        await writeFlattenedUpdateToFirebase(flattenedPriorityUpdate, userAuthToken, options);
      }

      setUpdateGroupProgress(updateGroup, { complete: true });
    } catch (err) {
      // If the error was a network error, abort the write and wait for the next online event to try again.
      if (err instanceof NetworkError) {
        throw err;
      }
      // Otherwise, mark this update group as having an error and continue with the next update group.
      else {
        updateGroupsThatErrored.add(updateGroup.id);
        setUpdateGroupProgress(updateGroup, { error: err });
      }
    }
  }
}

/**
 * Prepares and splits an update into priority and non-priority updates
 *
 * @param {Object} update - The update to be processed
 * @returns {{ flattenedNormalUpdate: Object, flattenedPriorityUpdate: Object }} An object containing separated non-priority and priority updates.
 */
function flattenAndSplitUpdateByPriority(update) {
  // Split the update into priority and non-priority updates
  const flattenedUpdate = Path.flattenObject(update, '/');
  const flattenedNormalUpdate = {};
  const flattenedPriorityUpdate = {};
  for (let path in flattenedUpdate) {
    if (path.endsWith('.priority')) flattenedPriorityUpdate[path] = flattenedUpdate[path];
    else flattenedNormalUpdate[path] = flattenedUpdate[path];
  }

  return { flattenedNormalUpdate, flattenedPriorityUpdate };
}

/**
 * Finishing writing the outgoing changes by clearing relevant sync flags,
 * updating sync status, and writing the updated sync key to Firebase.
 * @param {string} userAuthToken - The authentication token for the current user.
 */
async function finishWritingChanges(userAuthToken) {
  // Update error and unsaved changes flags only if there are no update groups that errored
  if (updateGroupsThatErrored.size === 0) {
    // Clear the flag for unsaved changes
    globalThis.FirebaseIDB.fm.set('unsavedChanges', false);

    // Clear any sync error flags since everything is now synced
    globalThis.FirebaseIDB.fm.set('syncError', null);
    await globalThis.FirebaseIDB.set('$syncError', null);
  } else {
    globalThis.FirebaseIDB.fm.set('unsavedChanges', true);
    globalThis.FirebaseIDB.fm.set('syncError', true);
    await globalThis.FirebaseIDB.set('$syncError', true);
  }

  // Clear the syncing flag since syncing is complete
  globalThis.FirebaseIDB.fm.set('syncing', false);

  // Set the synced flag if the user is online
  if (_userIsOnline) globalThis.FirebaseIDB.fm.set('synced', true);

  // Update the sync key in Firebase
  const currentDeviceId = globalThis.FirebaseIDB.fm.get('deviceId');
  const syncKeyUpdatePath = `photoheight/sync_keys/${currentDeviceId}`;
  const syncKeyUpdateData = { key: _syncKey, last_updated: firebase.database.ServerValue.TIMESTAMP };
  const syncKeyUpdate = { [syncKeyUpdatePath]: syncKeyUpdateData };
  const flattenedSyncKeyUpdate = Path.flattenObject(syncKeyUpdate, '/');
  await writeFlattenedUpdateToFirebase(flattenedSyncKeyUpdate, userAuthToken, { skipUpdatingOutgoingChanges: true });
}

/**
 * Convert "$" and ".sv" paths in an update to a format suitable for writing
 * @param {object} flattenedUpdate - The update object to sanitize
 * @returns {object} The sanitized update object
 */
function sanitizeFlattenedUpdate(flattenedUpdate) {
  // Create a new object to hold the sanitized update
  const sanitizedUpdate = {};

  // Process each path in the flattened update
  for (const path in flattenedUpdate) {
    const dataAtPath = flattenedUpdate[path];
    // Filter out paths that start with "$"
    if (path[0] === '$') continue;
    // If the path ends with ".sv", convert it to the server value format
    if (path.endsWith('.sv')) {
      const pathWithSvRemoved = Path.join(Path.split(path, '/').slice(0, -1), '/');
      sanitizedUpdate[pathWithSvRemoved] = { '.sv': dataAtPath };
    } else sanitizedUpdate[path] = dataAtPath;
  }

  return sanitizedUpdate;
}

/**
 * Validates and filters an update object based on sync rules defined in `globalThis.pathRules.syncToFirebase`.
 * For each path in the update, checks if a sync rule exists and evaluates its condition.
 * - If no sync rule exists for a path, the path is considered valid and included as-is.
 * - If a sync rule exists and its condition passes, the path is included.
 * - If the condition fails but a backup path is provided, the update is included under the backup path.
 *
 * @param {Object} update - The update object containing key-value pairs to be validated for syncing.
 * @returns {Promise<Object>} A new object containing only the validated and possibly remapped update paths.
 */
async function getValidatedUpdateForSyncing(update) {
  const validatedUpdate = {};
  for (const updatePath of Object.keys(update ?? {})) {
    // Find the sync rule for this path
    const syncRule = globalThis.pathRules.sync.find((rule) => rule.exp.test(updatePath));
    // If there's no sync rule, return the update as is
    if (!syncRule) {
      // No sync rule means the path is always valid
      validatedUpdate[updatePath] = update[updatePath];
      continue;
    }

    // Evaluate the sync rule's condition
    const validationResult = await syncRule.if({
      databaseURL: `https://${_databaseName}.firebaseio.com`,
      updatePath,
      value: update[updatePath],
      update,
      context: {}
    });
    const { backupPath, pass: canSyncPath } = validationResult;
    // If the path can be synced, include it in the validated update
    if (canSyncPath) validatedUpdate[updatePath] = update[updatePath];
    // If the path cannot be synced and has a backup path, include the backup path instead
    else if (backupPath) validatedUpdate[backupPath] = update[updatePath];
  }

  // Return the validated update
  return validatedUpdate;
}

/**
 * Send a PATCH request to the Firebase REST API to write the provided update object to the specified database.
 * @param {Object} update - The update object containing the data to be written.
 * @param {string} userAuthToken - The authentication token for Firebase.
 * @param {Object} [options] - Additional options for the write operation.
 * @param {boolean} [options.skipUpdatingOutgoingChanges] - A flag indicating if the outgoing changes should be updated
 * @param {string} [options.processActionTrackingForJobId] - Job id whose action tracking resolution must be verified before writing
 */
async function writeFlattenedUpdateToFirebase(update, userAuthToken, options = {}) {
  let writeIsSuccessful = false;
  _shouldValidatePathsToSync = false;
  // Keep trying to write the update while the user is online and the write has not been successful
  // Note: _userIsOnline can be true and the write can still fail due to network connectivity issues, so keep trying until it works
  while (!writeIsSuccessful && _userIsOnline) {
    try {
      // Sanitize the update before writing
      const sanitizedUpdate = sanitizeFlattenedUpdate(update);

      // Validate that the update paths are valid if firebase rejected them the first time
      const validatedSanitizedUpdate =
        _shouldValidatePathsToSync === true ? await getValidatedUpdateForSyncing(sanitizedUpdate) : sanitizedUpdate;

      // Log action tracking for job-scoped updates
      const processActionTrackingForJobId = options.processActionTrackingForJobId;
      if (processActionTrackingForJobId) await processActionTrackingForUpdate(validatedSanitizedUpdate, processActionTrackingForJobId);

      // Try to post the PATCH request to Firebase
      const updateEndpoint = `${_databaseName ? `https://${_databaseName}.firebaseio.com` : globalThis.firebaseConfig.databaseURL}/.json?auth=${userAuthToken}`;
      const body = JSON.stringify(validatedSanitizedUpdate);
      const res = await fetch(updateEndpoint, { method: 'PATCH', body });

      // If the response is OK, mark the write as successful
      if (res.ok) {
        writeIsSuccessful = true;
        // Clear the flag for validating paths since the write was successful
        _shouldValidatePathsToSync = false;
      }
      // Catch any response that isn't OK and throw an error
      else throw new Error(`Firebase REST API: ${res.status} ${res.statusText}`);
    } catch (e) {
      const enumeratedError = EnumerateError(e);
      // If the error is due to firebase rejecting invalid paths, set a flag so that it can be validated the next time through the while loop
      if (enumeratedError.message === 'Firebase REST API: 401 Unauthorized' && !_shouldValidatePathsToSync)
        _shouldValidatePathsToSync = true;
      // If the error not related to being offline, throw it
      else {
        // If there's an error unrelated to being offline, throw it
        const offlineErrorMessages = ['Load failed', 'Failed to fetch'];
        // Throw the error if it's not an offline related error
        const isOfflineError = offlineErrorMessages.some((offlineErrorMessage) => enumeratedError.message.includes(offlineErrorMessage));
        if (!isOfflineError) throw enumeratedError;
      }
    }

    await new Promise((x) => setTimeout(x, 100));
  }

  // If the loop exited without success (e.g. user went offline), throw so callers don't mark progress complete.
  if (!writeIsSuccessful) throw new NetworkError('Write interrupted: user went offline');

  // Clear all written paths in a single queued read/modify/write so paths added concurrently
  // (e.g. by another promotion or job clear) are preserved and the operation is atomic relative
  // to other outgoing-changes operations.
  const { skipUpdatingOutgoingChanges = false } = options;
  if (writeIsSuccessful && !skipUpdatingOutgoingChanges) {
    await queueOutgoingChangesOperation(async () => {
      const outgoingChanges = await getOutgoingChanges();
      if (Object.keys(outgoingChanges).length === 0) return;
      for (const pathToDelete of Object.keys(update)) {
        Path.delete(outgoingChanges, pathToDelete, '/');
      }
      await overwriteOutgoingChanges(outgoingChanges);
    });
  }
}

// ----------------- Define action tracking functions -----------------

/**
 * Track actions triggered by updates to the database. Uses the Firebase REST API to read and write action tracking data.
 * @param {Object} update - The update object containing the changes.
 * @param {string} jobId - The job id this update belongs to, whose action tracking must already be resolved
 */
async function processActionTrackingForUpdate(update, jobId) {
  // Refuse to process the update if action tracking has not been resolved for this job
  const actionTrackingForJobIsRegistered = _jobsWithRegisteredActionTracking[jobId];
  if (!actionTrackingForJobIsRegistered) throw new Error(`Action tracking has not been resolved for job ${jobId}`);
  const { actionTrackingModels, actionTrackingCompanyId } = actionTrackingForJobIsRegistered;

  // Determine if the update should trigger any actions based on the current models action tracking
  const actions = GetTriggeredActions('', update, actionTrackingModels);
  const updateHasTriggeredActions = Array.isArray(actions) && actions.length;
  if (!updateHasTriggeredActions) return;

  // Set up the necessary routes and authentication token for interacting with the Firestore REST API
  const apiUrl = 'https://firestore.googleapis.com/v1';
  const databaseRoute = `projects/${_databaseName}/databases/(default)`;
  const companyRoute = `${databaseRoute}/documents/companies/${actionTrackingCompanyId}`;

  // Set up the necessary user data to the fetch inputs for processing action tracking
  const authToken = await firebase.auth().currentUser.getIdToken();
  const currentUser = firebase.auth().currentUser;
  const { userGroup = null, rootCompany = null } = (await currentUser?.getIdTokenResult())?.claims ?? {};

  // Create an object to hold necessary data for interacting with the Firestore REST API for action tracking
  const fetchInputs = {
    databaseUrl: `${apiUrl}/${databaseRoute}`,
    companyUrl: `${apiUrl}/${companyRoute}`,
    companyRoute,
    authToken,
    currentUser,
    userGroup,
    rootCompany
  };

  // Process each triggered action
  const processActionPromises = actions.map((action) => applyActionConfigToActions(action, fetchInputs, actionTrackingCompanyId));
  await Promise.all(processActionPromises);
}

/**
 * Apply the given action config to all relevant actions in the action tracking firestore collection.
 * If there are collisions with existing actions, either overwrite them or set a flag in IndexedDB for the UI to prompt the user based on the action config's behaviors.
 * @param {object} actionConfig - The action config to apply to the action tracking collection
 * @param {object} fetchInputs - An object containing the necessary data to perform all fetch requests
 * @param {string} actionTrackingCompanyId - The company id whose action tracking collection is being written to
 */
async function applyActionConfigToActions(actionConfig, fetchInputs, actionTrackingCompanyId) {
  // Fetch any existing actions that match the job id, action id, and node id of the action being tracked
  const relatedActionsDocuments = await queryForAllRelatedActions(actionConfig, fetchInputs);

  // If there are no existing actions or the action allows multiple entries, create a new action tracking entry
  const canCreateNewAction = relatedActionsDocuments.length === 0 || actionConfig.behaviors?.allow_multiple;
  if (canCreateNewAction) {
    await createNewActionEntry(actionConfig, fetchInputs);
    return;
  }

  // Get the most recent action from the fetch results
  const mostRecentAction = getMostRecentAction(relatedActionsDocuments);

  // If there is an existing action and the action allows overwriting without prompting, overwrite the existing action with a new entry
  const { currentUser, userGroup, rootCompany } = fetchInputs;
  if (actionConfig.behaviors?.overwrite_without_prompt) {
    await OverwriteAction(
      mostRecentAction,
      {
        uid: currentUser?.uid,
        user_group: userGroup,
        root_company: rootCompany,
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        job_id: actionConfig.jobId,
        node_id: actionConfig.nodeId,
        type: actionConfig.type,
        action_id: actionConfig.$key,
        action_name: actionConfig.action_name,
        value: actionConfig.value,
        order: actionConfig.order != null ? actionConfig.order : null
      },
      actionTrackingCompanyId,
      firebase.firestore()
    );
  }

  // Set a flag in IndexedDB to indicate that there was a collision with the existing action
  else {
    globalThis.FirebaseIDB.fm.set('actionTrackingCollision', {
      newAction: {
        uid: currentUser?.uid,
        user_group: userGroup,
        root_company: rootCompany,
        job_id: actionConfig.jobId,
        node_id: actionConfig.nodeId,
        type: actionConfig.type,
        action_id: actionConfig.$key,
        action_name: actionConfig.action_name,
        value: actionConfig.value,
        order: actionConfig.order != null ? actionConfig.order : null
      },
      oldAction: mostRecentAction,
      itemType: 'node',
      behaviors: actionConfig.behaviors
    });
  }
}

/**
 * Queries all action entries and returns all actions that match the given action's job id, action id, and node id
 * @param {object} actionConfig - The action config to use when querying action entries
 * @param {object} fetchInputs - An object containing the necessary data to perform the fetch request
 * @returns {Promise<object[]>} An array of action documents that match the given action's job id, action id, and node id
 */
async function queryForAllRelatedActions(actionConfig, fetchInputs) {
  const { authToken, companyUrl } = fetchInputs;
  // This query is used to get all actions that match the job id, action id, and node id
  const queryFilters = [
    {
      fieldFilter: {
        field: {
          fieldPath: 'job_id'
        },
        op: 'EQUAL',
        value: {
          stringValue: actionConfig.jobId
        }
      }
    },
    {
      fieldFilter: {
        field: {
          fieldPath: 'action_id'
        },
        op: 'EQUAL',
        value: {
          stringValue: actionConfig.$key
        }
      }
    },
    {
      fieldFilter: {
        field: {
          fieldPath: 'node_id'
        },
        op: 'EQUAL',
        value: {
          stringValue: actionConfig.nodeId
        }
      }
    }
  ];

  // Build the main query to fetch actions
  const fetchQuery = {
    structuredQuery: {
      from: [{ collectionId: 'action_tracking' }],
      where: {
        compositeFilter: {
          op: 'AND',
          filters: queryFilters
        }
      }
    }
  };

  // Execute the query against the Firestore REST API
  const allFetchResults = await fetch(`${companyUrl}:runQuery`, {
    method: 'post',
    headers: { Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(fetchQuery)
  });
  const allFetchResultsJson = await allFetchResults.json();

  // Filter out any results that don't have a document (this can happen if there are empty results or if there are results that don't match the query)
  const filteredFetchResults = allFetchResultsJson.filter((x) => x.document != null);
  return filteredFetchResults;
}

/**
 * Create a new action tracking entry in Firestore using the REST API.
 * @param {object} actionConfig - The action config to create a new entry for
 * @param {object} fetchInputs - An object containing the necessary data to perform the fetch request
 */
async function createNewActionEntry(actionConfig, fetchInputs) {
  // Get the necessary data from fetch inputs
  const { currentUser, userGroup, rootCompany, authToken, databaseUrl, companyRoute } = fetchInputs;

  // Generate a new document ID for the action tracking entry
  const actionId = firebase.firestore().collection('tmp').doc().id;

  // Define the route for the new action tracking entry
  const actionRoute = `${companyRoute}/action_tracking/${actionId}`;

  // Define the type of the action's value
  const valueTypeMap = {
    string: 'stringValue',
    number: 'doubleValue',
    boolean: 'booleanValue',
    object: 'mapValue'
  };
  const actionValueType = valueTypeMap[typeof actionConfig.value];

  // Define all the actions data to be written to the new document
  const actionData = {
    uid: { stringValue: currentUser?.uid ?? null },
    user_group: { stringValue: userGroup },
    root_company: { stringValue: rootCompany },
    job_id: { stringValue: actionConfig.jobId },
    node_id: { stringValue: actionConfig.nodeId },
    type: { stringValue: actionConfig.type },
    action_id: { stringValue: actionConfig.$key },
    action_name: { stringValue: actionConfig.action_name },
    value: actionConfig.value != null ? { [actionValueType]: actionConfig.value } : { nullValue: null },
    order: actionConfig.order != null ? { integerValue: actionConfig.order } : { nullValue: null }
  };

  // Set up the transform to set the timestamp to the server time when the document is created
  const actionTransforms = [{ fieldPath: 'timestamp', setToServerValue: 'REQUEST_TIME' }];

  // Define the writes for the new action
  const firestoreWritesForAction = [
    { update: { name: actionRoute, fields: actionData } },
    { transform: { document: actionRoute, fieldTransforms: actionTransforms } }
  ];

  // Commit the new document and apply the writes to Firestore using the REST API
  await fetch(`${databaseUrl}/documents:commit`, {
    method: 'post',
    headers: { Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ writes: firestoreWritesForAction })
  });
}

/**
 * Get the most recent action from a list of Firestore documents returned by the REST API,
 * @param {object[]} actionDocuments - An array of documents returned by the Firestore REST API representing action tracking entries
 * @returns {object} - An object representing the most recent action
 */
function getMostRecentAction(actionDocuments) {
  const actions = actionDocuments.map(({ document }) => {
    const fields = {};
    for (const [key, value] of Object.entries(document.fields)) {
      if (value.stringValue !== undefined) {
        fields[key] = value.stringValue;
      } else if (value.doubleValue !== undefined) {
        fields[key] = parseFloat(value.doubleValue);
      } else if (value.booleanValue !== undefined) {
        fields[key] = Boolean(value.booleanValue);
      } else if (value.integerValue !== undefined) {
        fields[key] = parseInt(value.integerValue);
      } else if (value.nullValue !== undefined) {
        fields[key] = null;
      } else if (value.timestampValue !== undefined) {
        // convert this to a Firestore timestamp
        fields[key] = firebase.firestore.Timestamp.fromDate(new Date(value.timestampValue));
      } else {
        console.warn(`Unknown value type for key ${key}`, value);
      }
    }
    return {
      $key: document.name.split('/').pop(),
      ...fields
    };
  });

  // Sort the actions by timestamp and return the most recent one
  const sortedActions = actions.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis());
  return sortedActions.pop();
}
